// The roll's theatrical timing - how long each die tumbles - in one module,
// because TWO parties need the same numbers and used to guess at each other:
//
//   - GameTable deals the durations to the Die3d cubes;
//   - the store paces a bot's next decision, which must land AFTER the
//     slowest die but should not dawdle beyond it (Matt, 2026-08-29: "you
//     seem to have added a pretty long delay after the dice have landed
//     before the computers make their choices").
//
// The old arrangement paused for the WORST CASE (2.5s) on every roll, so a
// roll whose dice happened to land early sat in dead air. Durations are now
// DETERMINISTIC - hashed from the rolled dice plus the action counter - so
// both sides compute identical numbers independently, and the pause can hug
// the actual landing. Determinism buys a second thing for free: in an online
// game every client deals the same durations, so everyone watches the same
// roll.
//
// This is theatre, not state: nothing here touches the engine, and the
// engine rolled the moment the action dispatched.

export const ROLL_TUMBLE_MIN_MS = 1400;
export const ROLL_TUMBLE_MAX_MS = 2500;
export const ROLL_SETTLE_MS = 250;
// The worst case, still exported for anything that needs a static bound.
export const ROLL_ANIMATION_MAX_MS = ROLL_TUMBLE_MAX_MS + ROLL_SETTLE_MS;

// djb2 - tiny, stable, good enough to make four dice look uncorrelated.
const hash = (text) => {
  let h = 5381;
  for (let i = 0; i < text.length; i += 1) {
    h = ((h << 5) + h + text.charCodeAt(i)) >>> 0;
  }
  return h;
};

/**
 * Each die's tumble length for one specific roll. Same dice + same salt
 * (use meta.seq) = same four numbers, everywhere, every time.
 */
export function dealTumbleDurations (dice, salt = 0) {
  const spread = ROLL_TUMBLE_MAX_MS - ROLL_TUMBLE_MIN_MS;
  return dice.map((die, index) => {
    const h = hash(`${salt}:${index}:${die}:${dice.join('')}`);
    return ROLL_TUMBLE_MIN_MS + (h % (spread + 1));
  });
}

/**
 * How long THIS roll's animation runs, start to gate-lift: the slowest die
 * plus the settle beat. Zero when there are no dice showing, or when the
 * viewer skips animation entirely (reduced motion) - a pause for an
 * animation nobody is watching is just dead air.
 */
export function rollAnimationMs (game, mediaMatcher = typeof window !== 'undefined' ? window.matchMedia?.bind(window) : null) {
  // Camel Up rolls ONE die at a time, and only a state whose latest event
  // is a roll owes anyone an animation.
  const roll = game?.lastRoll;
  const last = game?.log?.[game.log.length - 1];
  if (!roll || last?.kind !== 'roll') return 0;
  if (mediaMatcher?.('(prefers-reduced-motion: reduce)')?.matches) return 0;
  return dealTumbleDurations([`${roll.camel}${roll.value}`], game.meta?.seq || 0)[0] + ROLL_SETTLE_MS;
}
